import React, { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Empty, message, Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import { enterShopApi, getDefaultShopApi, switchShopApi } from '@/api/shop/shop';
import {
  setCurrentShopId,
  setCurrentShopIsPop,
  setCurrentShopPlatformType,
  setCurrentShopType,
  setShopList,
} from '@/store';
import './index.scss';

const shopLogoFallback = 'https://img.alicdn.com/imgextra/i3/155168396/O1CN01mntiC82BtQTQhNu55_!!155168396.png';

const ShopSelect: React.FC = () => {
  const [shops, setShops] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [autoEnter, setAutoEnter] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const navigate = useNavigate();

  const handleDefaultShop = async (list: any[]) => {
    try {
      const res = await getDefaultShopApi();
      if (res?.code === 200 || res?.code === 0) {
        const data = res.data || {};
        if (typeof data.autoSelectNextTime === 'number') {
          setAutoEnter(data.autoSelectNextTime === 1);
        }
        if (typeof data.shopId === 'number') {
          setSelectedId(data.shopId);
        }
        if (data.autoSelectNextTime === 1 && data.shopId) {
          const exist = list.find(item => item.shopId === data.shopId);
          if (exist) {
            setCurrentShopId(data.shopId, exist.platformType);
            navigate('/');
          }
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchShops();
  }, []);

  const fetchShops = async (allowAutoJump = true) => {
    setLoading(true);
    try {
      const res = await switchShopApi();
      if (res?.code === 200) {
        const list = res.data || [];
        setShops(list);
        setShopList(list);
        if (allowAutoJump) {
          await handleDefaultShop(list);
        }
      } else {
        message.error(res.msg || '获取店铺列表失败');
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const groupedShops = useMemo(() => {
    const map: Record<string, any[]> = {};
    shops.forEach(shop => {
      const key = shop.platformType || '其他';
      if (!map[key]) {
        map[key] = [];
      }
      map[key].push(shop);
    });
    return map;
  }, [shops]);

  const handleEnterShop = async (shop: any) => {
    if (!shop?.shopId) {
      message.warning('店铺信息不完整');
      return;
    }
    setSelectedId(shop.shopId);

    try {
      if (autoEnter) {
        await enterShopApi({
          shopId: shop.shopId,
          autoSelectNextTime: 1,
          nextAutoEnterShopId: shop.shopId,
        });
      }
    } catch (error) {
      console.error(error);
      message.error('保存自动进入设置失败，请稍后重试');
    } finally {
      setCurrentShopIsPop(shop.isPop ?? null);
      setCurrentShopPlatformType(shop.platformType ?? null);
      setCurrentShopType(shop.shopType ?? null);
      setCurrentShopId(shop.shopId, shop.platformType);

      navigate('/');
    }
  };

  const formatPlatformLabel = (type?: string) => {
    if (type === 'TM') return '淘宝/天猫';
    if (type === 'JD') return '京东';
    return `【${type || '其他'}】`;
  };

  const renderGroups = () => {
    const keys = Object.keys(groupedShops);
    if (!keys.length) {
      return <Empty description="暂无可选择的店铺" />;
    }
    return keys.map(key => (
      <div
        className="shop-select-group"
        key={key}>
        <div className="shop-select-group__title">{formatPlatformLabel(key)}</div>
        <div className="shop-select-group__list">
          {groupedShops[key].map(shop => (
            <div
              className={`shop-select-card shop-select-card--${(shop.platformType || 'other').toLowerCase()} ${
                selectedId === shop.shopId ? 'is-active' : ''
              }`}
              key={`${key}-${shop.shopId}`}
              onClick={() => handleEnterShop(shop)}>
              <div className="shop-select-card__logo">
                <img
                  src={shop.shopImageOssId || shopLogoFallback}
                  alt={shop.shopName}
                />
              </div>
              <div className="shop-select-card__name">{shop.shopName}</div>
            </div>
          ))}
        </div>
      </div>
    ));
  };

  return (
    <div className="shop-select-page">
      <div className="shop-select-content">
        <Spin
          spinning={loading}
          tip="加载中...">
          {renderGroups()}
        </Spin>
        <div className="shop-select-auto">
          <Checkbox
            className="shop-select-checkbox"
            checked={autoEnter}
            onChange={e => setAutoEnter(e.target.checked)}>
            下次自动进入所选店铺
          </Checkbox>
        </div>
      </div>
    </div>
  );
};

export default ShopSelect;
